import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import {
  ExportTemplate,
  type ValidationErrorsSourceExportUnavailableReason,
} from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { getDomainErrorMessage } from '@/lib/utils/domain-error-translator';

import { useDealSelectionExport } from './useDealSelectionExport';

/**
 * `source` : le fichier importé par la marque avec les colonnes d'erreurs
 * (`VALIDATION_ERRORS_SOURCE`) ; `so` : l'export de la phase 1
 * (`VALIDATION_ERRORS`).
 */
export type ValidationErrorsExportType = 'source' | 'so';

const TEMPLATE_BY_EXPORT_TYPE: Record<
  ValidationErrorsExportType,
  ExportTemplate
> = {
  source: ExportTemplate.ValidationErrorsSource,
  so: ExportTemplate.ValidationErrors,
};

/** Pourquoi l'export `so` a été proposé à la place de `source`, pour le suivi. */
export type ValidationErrorsExportTracking = {
  unavailableReason?: ValidationErrorsSourceExportUnavailableReason | null;
};

/**
 * Nom du fichier donné par le backend à l'export `source`
 * (`<fichier importé>-erreurs.csv`), lu dans l'URL signée ; `null` sinon.
 */
export const sourceExportFileNameFromUrl = (
  downloadUrl: string
): string | null => {
  try {
    const segment = new URL(downloadUrl).pathname.split('/').pop() ?? '';
    const fileName = decodeURIComponent(segment);
    return fileName.endsWith('-erreurs.csv') ? fileName : null;
  } catch {
    return null;
  }
};

export function useExportValidationErrors(
  dealId: string,
  dealName: string,
  anomalyTypesCount: number,
  exportType: ValidationErrorsExportType = 'so'
) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { isExporting, downloadUrl, error, startExport } =
    useDealSelectionExport(dealId, TEMPLATE_BY_EXPORT_TYPE[exportType]);
  const [isInteracting, setIsInteracting] = useState(false);
  const downloadedUrlRef = useRef<string | null>(null);

  const exportValidationErrors = useCallback(
    (tracking?: ValidationErrorsExportTracking) => {
      trackEvent('VALIDATION_ERRORS_EXPORT_CLICKED', {
        dealId,
        anomalyTypesCount,
        exportType,
        ...(tracking?.unavailableReason && {
          unavailableReason: tracking.unavailableReason,
        }),
      });
      downloadedUrlRef.current = null;
      setIsInteracting(true);
      startExport();
    },
    [dealId, anomalyTypesCount, exportType, startExport]
  );

  useEffect(() => {
    if (!isInteracting || !downloadUrl) return;
    if (downloadedUrlRef.current === downloadUrl) return;
    downloadedUrlRef.current = downloadUrl;

    fetch(downloadUrl)
      .then((res) => {
        if (!res.ok) throw new Error(t('general.error_download'));
        return res.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const date = new Date().toISOString().slice(0, 10);
        a.download =
          (exportType === 'source'
            ? sourceExportFileNameFromUrl(downloadUrl)
            : null) ?? `${dealName}-Erreurs-${date}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast({
          title: t('general.success_export'),
          description: t('general.success_export_csv_download'),
        });
      })
      .catch(() => {
        toast({
          title: t('general.error'),
          description: t('general.error_download'),
          variant: 'destructive',
        });
      })
      .finally(() => setIsInteracting(false));
  }, [downloadUrl, isInteracting, dealName, exportType, t, toast]);

  useEffect(() => {
    if (!error) return;
    toast({
      title: t('general.error'),
      description: getDomainErrorMessage(
        error.message,
        t,
        'components.alert_sidebar.export_errors_failed'
      ),
      variant: 'destructive',
    });
    setIsInteracting(false);
  }, [error, t, toast]);

  return { exportValidationErrors, isExporting: isExporting || isInteracting };
}
